import { Box, Stack, Typography } from '@mui/material';
import { isUndefined } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Path, useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { Divider } from '@/components/Divider';
import {
  RadioGroupOption,
  SelectInputOption,
} from '@/components/form/baseInputs/inputTypes';
import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { Share04Icon } from '@/components/icons/Share04Icon';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { ContextualHelpTrigger } from '@/modules/content/components/ContextualHelpTrigger';
import { COLORS } from '@/styles/tokens/colors';
import {
  AccessParameterFrequency,
  AccessParameterKind,
  ScheduledDistributionFrequency,
} from '@/types/schema';

import { AccessParameterAgeRequirementForm } from './AccessParameterAgeRequirementForm';
import {
  ACCESS_PARAMETER_KINDS,
  SCHEDULED_DISTRIBUTION_FREQUENCY_ITEMS,
} from './EntityBeneficiariesSubform.constants';
import {
  BeneficiaryFormAccessParameter,
  DistributionType,
  EntityBeneficiaryAccessFrequencies,
  EntityBeneficiaryAccessFrequency,
} from './EntityBeneficiariesSubform.types';
import { GrowByInflationCheckbox } from './GrowByInflationCheckbox';

type PartialAccessParameterFormPaths = Path<BeneficiaryFormAccessParameter>;

const ACCESS_INTERVAL_OPTIONS: SelectInputOption<EntityBeneficiaryAccessFrequency>[] =
  [
    {
      value: EntityBeneficiaryAccessFrequencies.Ongoing,
      display: 'Ongoing',
    },
    { value: AccessParameterFrequency.Monthly, display: 'Monthly' },
    { value: AccessParameterFrequency.Quarterly, display: 'Quarterly' },
    {
      value: AccessParameterFrequency.Semiannually,
      display: 'Semiannually',
    },
    { value: AccessParameterFrequency.Annually, display: 'Annually' },
  ];

export enum PartialAccessParameterFormVariant {
  Default = 'DEFAULT',
  SpecificAmountOrPercentage = 'SPECIFIC_AMOUNT_OR_PERCENTAGE',
  TrustIncome = 'TRUST_INCOME',
}

export interface PartialAccessParameterFormProps
  extends AmountPercentSubformProps {
  variant?: PartialAccessParameterFormVariant;
  beneficiaryId: string;
}

export function PartialAccessParameterForm({
  fieldName,
  includeOngoingFrequencyOption = false,
  variant = PartialAccessParameterFormVariant.Default,
  beneficiaryId,
}: PartialAccessParameterFormProps) {
  const { control } = useFormContext<BeneficiaryFormAccessParameter>();

  return (
    <Card
      variant="filled"
      sx={{ p: 2, bgcolor: COLORS.GRAY[200] }}
      data-testid={`partial-access-parameter-${variant}`}
    >
      {variant ===
        PartialAccessParameterFormVariant.SpecificAmountOrPercentage && (
        <AmountPercentSubform
          fieldName={fieldName}
          includeOngoingFrequencyOption={includeOngoingFrequencyOption}
        />
      )}
      {variant === PartialAccessParameterFormVariant.TrustIncome && (
        <>
          <FormLayoutRow>
            <FormLayoutItem>
              <FormAwarePercentInput<BeneficiaryFormAccessParameter>
                control={control}
                fieldName={
                  `${fieldName}.percentage` as 'percentage' satisfies PartialAccessParameterFormPaths
                }
                label="Percentage of trust income"
                maxValue={100}
                allowNegative={false}
                decimalScale={0}
                required
                contextualHelp={
                  <PopperContent body="Input the percentage of trust income that this individual has access to" />
                }
                isDecimalJSInput
              />
            </FormLayoutItem>
          </FormLayoutRow>
          <DistributionTypeFormSegment
            fieldName={fieldName}
            showGrowByInflation={false}
          />
          <SubsectionDivider />
        </>
      )}
      <FormLayoutRow>
        <FormLayoutItem>
          <AccessParameterAgeRequirementForm
            fieldName={fieldName}
            beneficiaryId={beneficiaryId}
          />
        </FormLayoutItem>
      </FormLayoutRow>
    </Card>
  );
}

interface AmountPercentSubformProps {
  fieldName:
    | `values.${AccessParameterKind}` // The values object (HEMS, etc.)
    | `amountsOrPercentages.${number}`; // The amountsOrPercentages array (specific percent or amount)
  includeOngoingFrequencyOption?: boolean;
}

function AmountPercentSubform({
  fieldName,
  includeOngoingFrequencyOption,
}: AmountPercentSubformProps) {
  const { control, setValue } =
    useFormContext<BeneficiaryFormAccessParameter>();

  const [selectedKind, frequency, distributionType] = useWatch({
    name: [
      `${fieldName}.kind` as 'kind' satisfies PartialAccessParameterFormPaths,
      `${fieldName}.frequency` as 'frequency' satisfies PartialAccessParameterFormPaths,
      `${fieldName}.distributionType` as 'distributionType' satisfies PartialAccessParameterFormPaths,
    ],
    control,
  });

  const isPercentage = selectedKind === AccessParameterKind.Percentage;
  const isAmount = selectedKind === AccessParameterKind.Amount;

  useEffect(() => {
    setValue(
      `${fieldName}._hasFrequency` as '_hasFrequency' satisfies PartialAccessParameterFormPaths,
      frequency === EntityBeneficiaryAccessFrequencies.Ongoing ? false : true
    );
  }, [fieldName, frequency, setValue]);

  const frequencyOptions = useMemo<
    SelectInputOption<EntityBeneficiaryAccessFrequency>[]
  >(() => {
    if (distributionType === DistributionType.Scheduled) {
      return SCHEDULED_DISTRIBUTION_FREQUENCY_ITEMS;
    }
    return includeOngoingFrequencyOption
      ? ACCESS_INTERVAL_OPTIONS
      : ACCESS_INTERVAL_OPTIONS.filter(
          (option) =>
            option.value !== EntityBeneficiaryAccessFrequencies.Ongoing
        );
  }, [distributionType, includeOngoingFrequencyOption]);

  return (
    <>
      <FormLayoutRow>
        <FormLayoutItem width={12}>
          <Stack direction="row" spacing={2} alignItems="center" width="100%">
            <FormAwareButtonGroup<BeneficiaryFormAccessParameter>
              label="Access parameter kind"
              hideLabel
              fieldName={
                `${fieldName}.kind` as 'kind' satisfies PartialAccessParameterFormPaths
              }
              control={control}
              options={ACCESS_PARAMETER_KINDS}
            />
            <Box
              sx={{
                flexGrow: 1,
                flexShrink: 1,
              }}
            >
              {isPercentage && (
                <FormAwarePercentInput<BeneficiaryFormAccessParameter>
                  required
                  label="Access percentage"
                  hideLabel
                  fieldName={
                    `${fieldName}.percentage` as 'percentage' satisfies PartialAccessParameterFormPaths
                  }
                  control={control}
                  isDecimalJSInput
                  decimalScale={2}
                  data-testid="percentage"
                />
              )}
              {isAmount && (
                <FormAwareCurrencyInput<BeneficiaryFormAccessParameter>
                  required
                  label="Access amount"
                  hideLabel
                  fieldName={
                    `${fieldName}.amount` as 'amount' satisfies PartialAccessParameterFormPaths
                  }
                  control={control}
                  isDecimalJSInput
                  data-testid="amount"
                />
              )}
            </Box>
            <FormAwareSelectInput<BeneficiaryFormAccessParameter>
              control={control}
              fieldName={
                `${fieldName}.frequency` as 'frequency' satisfies PartialAccessParameterFormPaths
              }
              label="Frequency"
              options={frequencyOptions}
              hideLabel
              required
            />
          </Stack>
        </FormLayoutItem>
      </FormLayoutRow>
      <DistributionTypeFormSegment
        fieldName={fieldName}
        showGrowByInflation={isAmount}
      />
      <SubsectionDivider />
    </>
  );
}

function DistributionTypeTooltip() {
  return (
    <ContextualHelpTooltip>
      <PopperContent
        body={
          <Stack spacing={2}>
            <Typography variant="body-xs" component="p">
              Select <b>Discretionary distribution</b> to assume that the funds
              stay in the trust and will grow at the rate at which the trust
              grows.
            </Typography>
            <Typography variant="body-xs" component="p">
              Select <b>Scheduled distribution</b> to assume that the funds are
              immediately distributed to the beneficiary upon gaining access and
              will deplete assets from the trust by the amount specified at the
              end of each year to account for growth and cash flows.
            </Typography>
            <Box>
              <ContextualHelpTrigger
                contextualHelpId="beneficiaryReporting"
                display="Learn more"
                endIcon={<Share04Icon size={14} />}
              />
            </Box>
          </Stack>
        }
      />
    </ContextualHelpTooltip>
  );
}

const DISTRIBUTION_TYPE_RADIO_GROUP_KINDS: RadioGroupOption<DistributionType>[] =
  [
    {
      label: (
        <Stack direction="row" spacing={1} sx={{ whiteSpace: 'nowrap' }}>
          Discretionary distribution
          <DistributionTypeTooltip />
        </Stack>
      ),
      value: DistributionType.Discretionary,
    },
    {
      label: (
        <Stack direction="row" spacing={1} sx={{ whiteSpace: 'nowrap' }}>
          Scheduled distribution
          <DistributionTypeTooltip />
        </Stack>
      ),
      value: DistributionType.Scheduled,
    },
  ];

function SubsectionDivider() {
  return (
    <Box width="100%" sx={{ pb: 2 }}>
      <Divider
        sx={{
          borderColor: COLORS.GRAY[300],
        }}
      />
    </Box>
  );
}

interface DistributionTypeFormSegmentProps {
  fieldName:
    | `values.${AccessParameterKind}` // The values object (HEMS, etc.)
    | `amountsOrPercentages.${number}`; // The amountsOrPercentages array (specific percent or amount)
  showGrowByInflation: boolean;
}
function DistributionTypeFormSegment({
  fieldName,
  showGrowByInflation,
}: DistributionTypeFormSegmentProps) {
  const { control, setValue } =
    useFormContext<BeneficiaryFormAccessParameter>();

  const [distributionType, frequency] = useWatch({
    control,
    name: [
      `${fieldName}.distributionType` as 'distributionType' satisfies PartialAccessParameterFormPaths,
      `${fieldName}.frequency` as 'frequency' satisfies PartialAccessParameterFormPaths,
    ],
  });
  const previousDistributionType = usePrevious(distributionType);

  useEffect(() => {
    if (isUndefined(previousDistributionType)) return;
    if (previousDistributionType === distributionType) return;

    if (
      frequency === EntityBeneficiaryAccessFrequencies.Ongoing &&
      distributionType === DistributionType.Scheduled
    ) {
      setValue(
        `${fieldName}.frequency` as 'frequency' satisfies PartialAccessParameterFormPaths,
        ScheduledDistributionFrequency.OneTime
      );
    } else if (
      frequency === ScheduledDistributionFrequency.OneTime &&
      distributionType === DistributionType.Discretionary
    ) {
      setValue(
        `${fieldName}.frequency` as 'frequency' satisfies PartialAccessParameterFormPaths,
        EntityBeneficiaryAccessFrequencies.Ongoing
      );
    }
  }, [
    distributionType,
    fieldName,
    frequency,
    previousDistributionType,
    setValue,
  ]);

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={2}
      alignItems="center"
      justifyContent="space-between"
      mb={2}
    >
      <Box>
        <FormAwareRadioGroup<BeneficiaryFormAccessParameter>
          control={control}
          fieldName={
            `${fieldName}.distributionType` as 'distributionType' satisfies PartialAccessParameterFormPaths
          }
          label="Distribution type"
          options={DISTRIBUTION_TYPE_RADIO_GROUP_KINDS}
          row
          hideLabel
        />
      </Box>
      {showGrowByInflation && (
        <GrowByInflationCheckbox fieldName={`${fieldName}.growByInflation`} />
      )}
    </Stack>
  );
}
